/**
 * GA4 da VibX carregado por `gtag.js` DIRETO — não por uma tag de configuração
 * dentro do container do GTM.
 *
 * A razão é o measurement ID ser compartilhado: `G-LJBQHCYD3C` é o mesmo
 * stream do `vibx.com.br`, que já carrega o `gtag.js` direto. Configuração do
 * lado de fora é o arranjo que o `vibx-lp` usa, e repeti-lo aqui mantém os
 * dois com um emissor só cada.
 *
 * ARMADILHA, se alguém for mexer no `GTM-KBF6J9QS`: uma tag de configuração do
 * GA4 lá dentro para este mesmo measurement ID vira um SEGUNDO emissor — o
 * `gtag.js` direto e a tag do container — e dobra a medição deste app em
 * silêncio, sem erro em lugar nenhum. Tags de evento (GA4 Event) podem ser
 * criadas à vontade: herdam a configuração do `gtag.js` já carregado. O que
 * não pode é uma segunda CONFIGURAÇÃO.
 *
 * Dá para conferir sem acesso ao GTM — a configuração do container é pública:
 *
 *     curl -s "https://www.googletagmanager.com/gtm.js?id=GTM-KBF6J9QS" \
 *       | grep -oE '"function":"__[a-z_0-9]+"' | sort | uniq -c
 *
 * `__gaawe` é tag de evento e pode aparecer à vontade. `__gaawc` e `__googtag`
 * são os dois aliases da tag de configuração — a antiga e a atual — e é a
 * presença de um deles que quebra a premissa daqui. Em 03/09/2026 o container
 * tinha DUAS `__googtag`, uma delas para este measurement ID — ou seja, a
 * armadilha acima está armada agora, e este app conta cada `page_view` duas
 * vezes até ela ser removida. A remoção é do lado do GTM e não depende deste
 * arquivo; ela é segura porque as tags de evento herdam do `gtag.js` daqui.
 *
 * O ID é `NEXT_PUBLIC_*`, inlined no build: trocar exige rebuild da imagem,
 * não é toggle de runtime.
 *
 * QUEM CONTA AS NAVEGAÇÕES: este módulo, e só ele. A medição otimizada do
 * stream tinha "alterações na página de acordo com os eventos do histórico de
 * navegação" LIGADA, e era ela quem emitia `page_view` a cada `pushState` do
 * Next. A opção foi desligada de propósito em 03/09/2026: ela também contava as
 * reescritas de URL que não são página nova, e inflava a visualização de toda
 * aplicação de página única. Desde então quem não disparar, não é contado — e é
 * por isso que `trackNavigation` envia em vez de ficar quieto.
 *
 * A ARMADILHA ESPELHADA: se alguém religar aquela opção no stream, as
 * navegações voltam a ser contadas por fora E por aqui, e a medição dobra de
 * novo — mesmo com o container limpo. As duas pontas precisam concordar, e a
 * decisão registrada é: o app dispara, o stream não.
 */

/** `G-` + 4–20 alfanuméricos. Os measurement IDs de hoje são `G-` + 10. */
const MEASUREMENT_ID_PATTERN = /^G-[A-Z0-9]{4,20}$/i;

/**
 * Validado, e não repassado cru: o valor entra na query string do `src` do
 * loader, então um valor não validado seria um ponto de injeção alimentado pela
 * configuração de build. Qualquer coisa fora do formato conta como NÃO
 * configurado — nenhuma tag, em vez de uma tag que parece instalada e não
 * coleta nada.
 *
 * A leitura de `process.env` está escrita por extenso de propósito: o Next
 * substitui `process.env.NEXT_PUBLIC_X` textualmente no build do cliente e não
 * enxerga acesso dinâmico (`process.env[nome]`), que sairia como `undefined`.
 */
export const gaMeasurementId = (() => {
  const value = process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID?.trim();
  return value && MEASUREMENT_ID_PATTERN.test(value) ? value : null;
})();

declare global {
  interface Window {
    /*
      Os DOIS formatos convivem nesta fila, e o tipo precisa admitir os dois.

      O `gtag` empurra `arguments` — daí o `IArguments`. Mas o `dataLayer` é do GTM antes de ser do
      gtag, e evento personalizado entra como objeto simples (`{ event: 'nome' }`) — é assim que o
      snippet oficial documenta e é o que `utils/gtmEvent.ts` usa para acionar as tags do container.

      Declarar só `IArguments[]` fazia o TypeScript recusar o push de objeto, que é o caso mais
      comum de todos.
    */
    dataLayer?: (IArguments | Record<string, unknown>)[];
    gtag?: (...args: unknown[]) => void;
  }
}

let initialized = false;

/**
 * Uma página vista antes de a biblioteca existir. URL e título andam juntos
 * porque são congelados no mesmo instante: quando a fila for drenada, o
 * documento já será outro, e o gtag preencheria o título com o da página em que
 * o usuário estiver então — o título de uma página colado no endereço de outra.
 */
type PendingPageView = {
  page_location: string;
  page_title?: string;
  page_referrer?: string;
};

/**
 * A última página reportada, que é a referência da PRÓXIMA.
 *
 * `document.referrer` não serve aqui: numa navegação client-side ele continua
 * sendo o referenciador do documento original, então `/a` → `/b` → `/c`
 * reportaria a mesma origem externa nas três — e a página anterior, que é a
 * pergunta que "página de referência" responde, sumiria do relatório. Era a
 * medição otimizada quem preenchia isso ao contar por evento de histórico;
 * assumindo a contagem, este módulo assume também a corrente.
 *
 * Começa na página de entrada, gravada pelo `config`. Cada `page_view` de
 * navegação sai com a anterior e vira a anterior do seguinte — a corrente é
 * montada no momento da navegação, não no do envio, então uma drenagem tardia
 * da fila não a embaralha.
 */
let lastLocation: string | null = null;

/**
 * O título só entra congelado se já existir. Vazio não é um título, é a
 * ausência de um — e mandá-lo assim fixaria a ausência: o gtag preencheria
 * sozinho na hora do envio se a chave não estivesse lá.
 *
 * Acontece de valer: quem escreve o título é o `<Head>` do `_app`, por um
 * efeito, e efeito de descendente roda antes do efeito do próprio `_app` — mas
 * isso é uma ordem que se pode perder numa refatoração, e o custo de perdê-la
 * seria silencioso.
 */
const frozenTitle = () =>
  document.title ? { page_title: document.title } : {};

/**
 * Navegações que aconteceram antes de existir runtime do Google na página.
 * Enquanto não há quem processe o `dataLayer`, um envio não vai a lugar nenhum
 * de imediato; a fila guarda esses `page_view` com URL e título congelados e os
 * solta na ordem quando o runtime aparece.
 */
/**
 * A URL de entrada, capturada na avaliação do módulo — que acontece no
 * carregamento do bundle, antes da hidratação e portanto antes de qualquer
 * clique.
 *
 * Não pode ser lida dentro de `initGoogleAnalytics`: aquilo roda num efeito do
 * `_app`, e efeito é passivo, só corre depois da pintura. Um clique num link já
 * hidratado naquela janela executa o `pushState`, que é síncrono, e ali
 * `location` já é o DESTINO — a página de entrada seria reportada com o
 * endereço da seguinte, e a verdadeira não apareceria em lugar nenhum.
 */
const entryLocation =
  typeof window !== 'undefined' ? window.location.href : null;

const pending: PendingHit[] = [];

/**
 * Um envio à espera de runtime: `page_view` ou evento de produto, na MESMA fila.
 *
 * Uma fila só, e não uma por tipo: a ordem entre "a pessoa chegou nesta tela" e "a pessoa fez isto
 * aqui" é justamente a leitura que estes eventos existem para permitir. Com duas filas drenadas em
 * sequência, todo `page_view` sairia antes de todo evento — e um `nova_comunidade_aberta` anterior
 * ao redirecionamento da retomada apareceria DEPOIS do `page_view` do destino, invertendo o funil.
 */
type PendingHit = { name: string; params: Record<string, unknown> };

/**
 * Quem está usando o app, para o GA4 deixar de contar SESSÃO e passar a contar PESSOA.
 *
 * É o `platform_user.id` — número, nunca e-mail ou documento. Sem ele não há como cruzar
 * comportamento de tela com o banco: o relatório mostra que sessões somem e nunca quem sumiu, e a
 * coorte de cadastro, o canal de aquisição e a categoria declarada ficam de fora de qualquer
 * segmentação.
 *
 * Três estados, e a diferença importa: `undefined` é "ninguém resolveu a sessão ainda", `null` é
 * "não há ninguém logado" e a string é o id. Ver `withIdentity` para por que o anônimo CONHECIDO
 * precisa ser carimbado.
 *
 * Guardado no módulo porque a sessão pode resolver ANTES ou DEPOIS de `initGoogleAnalytics`: quando
 * chega antes, entra no próprio `config`; quando chega depois, vai num `set`. Nos dois casos o valor
 * persiste para os eventos seguintes — que é o comportamento documentado do `config`/`set`, e aqui
 * ele é desejado (diferente de `page_location`, que por isso NÃO vai no `config`).
 */
let currentUserId: string | null | undefined = undefined;

let libraryReady = false;

/**
 * `initGoogleAnalytics` chegou a rodar. Fora de produção ela não roda — e sem
 * esta marca a fila continuaria enchendo numa sessão de staging ou de
 * desenvolvimento sem NUNCA ser drenada, porque nada lá carrega a biblioteca.
 */
let started = false;

/**
 * Teto da fila. Ela existe para cobrir os segundos entre o `config` e a
 * biblioteca; se algo a manteve enchendo além disso — um bloqueador de
 * anúncios, uma CDN fora do ar, um request pendurado —, os eventos mais antigos
 * já perderam o sentido e o que não pode acontecer é a memória crescer sem fim.
 * É a única proteção contra isso, e de propósito: qualquer desistência mais
 * cedo jogaria fora páginas que o runtime do container ainda poderia enviar.
 */
const PENDING_LIMIT = 50;

/**
 * Um `page_view` de navegação client-side. A URL vai explícita porque o valor
 * foi congelado no instante da navegação — na hora do envio o documento já pode
 * ser outro.
 */
const sendHit = (hit: PendingHit) => {
  window.gtag?.('event', hit.name, hit.params);
};

/**
 * Envia agora, ou guarda na fila com o teto — a mesma fronteira para os dois tipos de envio.
 *
 * A fila vive na memória do documento: um recarregamento durante a espera pelo runtime leva junto o
 * que estava nela, e isso é aceito — ver a nota sobre repetição em `useCommunityDraftAnalytics`.
 */
/**
 * A identidade de QUEM disparou este envio, congelada no momento em que ele é criado.
 *
 * O `set` de identidade vai direto para o `dataLayer`, enquanto um envio pode ficar esperando o
 * runtime na fila deste módulo. Sem o carimbo, um envio criado por uma conta e drenado depois de a
 * sessão vencer (ou de outra conta entrar) sairia atribuído a quem não o produziu — janela curta,
 * mas silenciosa e impossível de perceber no relatório.
 *
 * ANÔNIMO CONHECIDO é carimbado também, e é a razão de `currentUserId` distinguir `null` de
 * `undefined`: depois de um logout, um envio sem carimbo herdaria o `set` da PRÓXIMA conta a
 * entrar, atribuindo a ela navegação que não foi dela. `undefined` (ninguém resolveu a sessão
 * ainda) é o único caso que segue sem carimbo, porque ali não há o que afirmar.
 */
const withIdentity = (params: Record<string, unknown>) =>
  currentUserId === undefined ? params : { user_id: currentUserId, ...params };

const sendOrQueue = (hit: PendingHit) => {
  if (libraryReady) {
    sendHit(hit);
    return;
  }

  pending.push(hit);
  if (pending.length > PENDING_LIMIT) pending.shift();
};

/**
 * Chamado quando algum runtime do Google passa a processar a configuração deste
 * measurement ID. A partir daí a fila não tem mais razão de ser: o que vier
 * pode ser enviado na hora. Idempotente porque tem duas origens possíveis, e a
 * primeira que chegar é a verdadeira.
 */
const markLibraryReady = () => {
  if (libraryReady) return;
  libraryReady = true;

  pending.splice(0).forEach(sendHit);
};

/**
 * Carrega o `gtag.js` e configura o measurement ID. Idempotente: o `_app`
 * monta uma vez, mas o guarda protege contra um remount em desenvolvimento
 * (Strict Mode roda o efeito duas vezes) virar duas configurações.
 *
 * O `config` dispara o primeiro `page_view` sozinho — a página de entrada. As
 * navegações seguintes são responsabilidade de `trackNavigation`: a contagem
 * por evento de histórico está desligada no stream, então ninguém mais as vê.
 */
export const initGoogleAnalytics = () => {
  if (!gaMeasurementId || initialized) return;
  initialized = true;
  started = true;

  window.dataLayer = window.dataLayer || [];
  // Function declaration que empurra o próprio `arguments` — é a forma exata do
  // snippet do Google, e é ela que a biblioteca detecta e assume ao carregar.
  // O que entra na fila tem de ser o objeto `arguments`, não um array: a
  // biblioteca distingue os dois ao drenar.
  function gtag() {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer?.push(arguments);
  }
  // O cast vem depois, em vez de parâmetros nomeados na declaração: o corpo lê
  // `arguments` e não usaria nenhum deles, então declará-los seria descrever
  // uma assinatura que a função não tem.
  const send = gtag as unknown as NonNullable<Window['gtag']>;
  window.gtag = window.gtag || send;

  // A fila é montada ANTES de o loader entrar no documento, então não há
  // corrida: quando o `gtag.js` executa, `js` e `config` já estão nela.
  send('js', new Date());
  /*
    O `config` NÃO leva `page_location` nem `page_title`. Parâmetro passado ali
    é persistente para o destino: ele acompanharia todo evento seguinte, não só
    o `page_view` de entrada — e as três tags de evento do funil de cadastro,
    dentro do `GTM-KBF6J9QS`, herdam esta mesma configuração. Um `envio form -
    cadastro usuario` disparado depois de duas navegações sairia carimbado com a
    URL e o título da página de entrada.

    A entrada vira um evento explícito, logo abaixo, e o congelamento passa a
    valer só para ela.
  */
  send('config', gaMeasurementId, {
    send_page_view: false,
    // Persistente de propósito: identidade vale para todos os eventos seguintes.
    ...(currentUserId ? { user_id: currentUserId } : {}),
  });

  /*
    A página de entrada. Sai sem `page_referrer` de propósito: ela tem um
    referenciador de verdade, o `document.referrer`, e é ele que o gtag lê
    sozinho.

    A URL vem do escopo do módulo — anterior a qualquer clique. O título vem de
    agora, e essa diferença é deliberada: quem o escreve é o `<Head>` do `_app`,
    por um efeito, e na avaliação do módulo ele ainda não existe.
  */
  if (entryLocation) {
    send('event', 'page_view', {
      page_location: entryLocation,
      ...frozenTitle(),
    });
    // Primeiro elo da corrente de referência.
    lastLocation = entryLocation;
  }

  /*
    Quem termina primeiro não é sabido de antemão, e não pode ser: o container
    do GTM que o `_app` carrega traz o MESMO runtime do Google e é capaz de
    processar este `config` — os dois dividem o `dataLayer`. Se ele chegasse
    antes, a fila daqui continuaria enchendo depois de já haver quem enviasse,
    e cada `page_view` esperaria por um evento que não precisava esperar.

    O callback de `get` responde exatamente à pergunta certa: ele só roda quando
    ALGUM runtime processou a configuração deste measurement ID, seja o do
    container, seja o do `gtag.js` abaixo.
  */
  send('get', gaMeasurementId, 'client_id', markLibraryReady);

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(
    gaMeasurementId,
  )}`;
  /*
    Carregar o loader é a outra forma de haver runtime na página, e por isso
    também destrava a fila. Note que ele NÃO passa a contar navegação: a
    contagem por evento de histórico está desligada no stream, então de ponta a
    ponta é `trackNavigation` quem emite — antes daqui pela fila, depois daqui
    na hora.
  */
  // Rede de segurança: se o `get` não for processado — container bloqueado,
  // configuração recusada —, o carregamento do próprio loader ainda destrava a
  // fila. `markLibraryReady` é idempotente, então os dois caminhos podem correr.
  script.onload = markLibraryReady;
  /*
    Sem `onerror`, de propósito. Este request falhar não significa que não há
    runtime: o container do GTM sobe por um caminho independente e pode
    processar a configuração — foi justamente para isso que a prontidão passou a
    vir do `get`. Descartar a fila aqui jogaria fora páginas que o container
    ainda seria capaz de enviar. Contra o crescimento sem fim quem responde é o
    teto da fila, que vale para qualquer motivo de demora.
  */
  document.head.appendChild(script);

  /*
    A navegação que ninguém viu.

    Esta função roda num efeito do `_app`, e o listener de `routeChangeComplete`
    é instalado por OUTRO efeito — os dois passivos, os dois depois da pintura.
    Um clique naquela janela navega antes de existir quem escutasse, então
    aquele `routeChangeComplete` não chegou a `trackNavigation`.

    O evento de entrada acima reportou a entrada, que é o valor certo para ela.
    Mas a página que o usuário está vendo agora é outra, e sem isto ela não
    sairia até a próxima navegação. A comparação detecta o caso e o repõe, com a
    entrada como referência — a corrente fica igual à que teria saído se o
    listener já estivesse lá.
  */
  const atual = window.location.href;
  if (entryLocation && atual !== entryLocation) {
    trackNavigation(
      `${window.location.pathname}${window.location.search}${window.location.hash}`,
    );
  }
};

/**
 * Registra uma navegação client-side — TODA ela, não só as anteriores ao
 * carregamento. Com a contagem por evento de histórico desligada no stream,
 * este é o único emissor de `page_view` de navegação do app; ficar quieto
 * depois que a biblioteca chega significaria medir a página de entrada e mais
 * nada.
 *
 * A fila continua existindo para a janela em que ainda não há runtime do Google
 * na página: ali o envio esperaria de todo modo, e enfileirar com URL e título
 * congelados é o que impede que uma sequência de redirecionamentos numa conexão
 * ruim — o de permissão, por exemplo — saia com o endereço da página errada.
 * Depois que existe runtime, a fila não tem mais função e o envio é direto.
 *
 * Só recebe navegações do router. Um `history.replaceState` avulso — a limpeza
 * de parâmetros de campanha, por exemplo — não passa por aqui, e é justamente
 * essa a diferença que fez a contagem do stream ser desligada: ela observava a
 * reescrita como página nova, e este módulo não. Não é uma página nova, é a
 * mesma página com a URL arrumada.
 *
 * Cada envio carrega a página anterior em `page_referrer` — ver `lastLocation`.
 */
export const trackNavigation = (url: string) => {
  if (!gaMeasurementId || !started) return;

  // O título vai junto e agora: se este `page_view` for para a fila, na hora do
  // envio o documento já será outro. É o melhor valor disponível —
  // `routeChangeComplete` é o ponto mais próximo do fim da navegação a que este
  // código tem acesso.
  const pageView: PendingPageView = {
    page_location: `${window.location.origin}${url}`,
    ...frozenTitle(),
    ...(lastLocation ? { page_referrer: lastLocation } : {}),
  };
  lastLocation = pageView.page_location;

  sendOrQueue({ name: 'page_view', params: withIdentity(pageView) });
};

/**
 * Diz ao GA4 QUEM está usando o app.
 *
 * Recebe o `platform_user.id`. Idempotente: a sessão é relida a cada leitura de sessão do NextAuth,
 * e reenviar o mesmo id a cada render seria ruído puro.
 *
 * `undefined` é "ainda não sei" — sessão carregando — e não muda nada. `null` é "não há mais
 * ninguém logado" e LIMPA a identidade dos envios seguintes: a sessão do NextAuth dura quatro horas
 * e pode vencer com a pessoa parada numa rota híbrida, e sem a limpeza toda navegação anônima
 * seguinte continuaria atribuída à conta anterior pelo resto da vida do documento. O que já foi
 * enviado não muda — isso o GA4 não desfaz, e não é o que se está tentando corrigir.
 */
export const setAnalyticsUserId = (userId?: string | number | null) => {
  if (userId === undefined) return;

  const id = userId === null ? null : String(userId);
  if (id === currentUserId) return;

  currentUserId = id;

  // Antes de `initGoogleAnalytics` não há `window.gtag`; o valor entra no `config` quando ele rodar.
  if (started) window.gtag?.('set', { user_id: id });
};

/**
 * Um evento de produto — o que a pessoa FEZ, não onde ela esteve.
 *
 * Existe porque a área do criador media só navegação, e navegação não distingue quem olhou de quem
 * tentou: na coorte de 11.439 cadastros entre 2026-03 e 2026-08, 708 criaram comunidade e 9.866
 * nunca voltaram depois do dia do cadastro — e a decisão acontece na PRIMEIRA sessão (89% de quem
 * cria, cria em até 24h), que é exatamente a sessão sobre a qual não se sabia nada.
 *
 * Mesma fronteira do `page_view`: antes do runtime do Google o evento espera na fila em vez de
 * sumir. E o mesmo teto, pelo mesmo motivo.
 *
 * ## Por que NÃO exige `started`, ao contrário do `page_view`
 *
 * Porque o primeiro evento do fluxo é disparado por um efeito de PÁGINA, e efeito de descendente
 * roda antes do efeito do `_app` — que é quem chama `initGoogleAnalytics`. Numa carga direta de
 * `/nova-comunidade`, exigir `started` descartaria o `nova_comunidade_aberta` para sempre, enquanto
 * a mesma tela alcançada por navegação interna o registraria: o denominador do funil ficaria
 * enviesado contra quem entra pela URL, que é justamente quem vem de anúncio.
 *
 * Fora de produção `initGoogleAnalytics` não roda, e a fila para de crescer no `PENDING_LIMIT` —
 * é ele, e não a marca `started`, que impede a memória de crescer sem fim.
 *
 * ## O contexto de página vai congelado
 *
 * Pelo mesmo motivo do `page_view`: se o envio esperar na fila, na hora da drenagem o documento
 * pode ser outro — a checagem de rascunho ativo redireciona `/nova-comunidade` para
 * `/nova-comunidade/[id]` — e o gtag preencheria `page_location` com o DESTINO, atribuindo a
 * abertura do fluxo à página seguinte.
 *
 * NUNCA carregar PII em parâmetro: identificador de tela, etapa e código de erro — nada de nome,
 * e-mail, documento ou token.
 *
 * A identidade de quem disparou vai carimbada no próprio envio — ver `withIdentity`.
 */
export const trackEvent = (
  name: string,
  params: Record<string, unknown> = {},
) => {
  if (!gaMeasurementId) return;

  sendOrQueue({
    name,
    params: withIdentity({
      page_location: window.location.href,
      ...frozenTitle(),
      ...params,
    }),
  });
};
